<script setup lang="ts">
import type { EventAttendeeResponse, InterestRung } from '@@/types/api';
import { useEventsApi } from '~/composables/api/use-events-api';
import { useAttendeesApi } from '~/composables/api/use-attendees-api';
import { ApiError } from '~/composables/api/api-error';
import { useEventEnrichment } from '~/composables/use-event-enrichment';
import { useCommitmentActions } from '~/composables/use-commitment-actions';
import { useCommitResume } from '~/composables/use-commit-resume';
import { usePurchaseActions } from '~/composables/use-purchase-actions';
import { useMetaPixel } from '~/composables/use-meta-pixel';
import { useTypeSwitchActions } from '~/composables/use-type-switch-actions';
import { useInviteActions } from '~/composables/use-invite-actions';
import { useInterestActions } from '~/composables/use-interest-actions';
import { useInterestResume } from '~/composables/use-interest-resume';
import { useMyInterests } from '~/stores/use-my-interests';
import { useAuthStore } from '~/stores/use-auth-store';
import { formatDateRange } from '~/composables/format-date-range';
import {
  ctaFor,
  isTicketSalesClosed,
  nativeInterestControlFor,
} from '~/utils/event-policy';
import {
  buildPageDescription,
  stripMarkdownToPlain,
} from '~/utils/sanitize-meta-content';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

definePageMeta({
  validate: (route) => UUID_RE.test(String(route.params.id)),
});

const config = useRuntimeConfig();
const route = useRoute();
const requestOrigin = config.public.siteUrl || useRequestURL().origin;
const eventId = computed(() => String(route.params.id));

const { data, status, refresh } = useAsyncData(
  `event-detail-${eventId.value}`,
  async () => {
    try {
      return await useEventsApi().getEvent(eventId.value);
    } catch (e) {
      // On client, 403/404 is expected for drafts — enrichment will
      // retry with auth. Return null to avoid a race condition where
      // useAsyncData's error handler overwrites enriched data.
      if (
        import.meta.client &&
        e instanceof ApiError &&
        (e.isForbidden || e.isNotFound)
      ) {
        return null;
      }
      throw e;
    }
  },
  { server: !config.public.mock },
);

const event = computed(() => data.value ?? null);

// Fire a Meta pixel ViewContent when the event id populates. Watching the id
// (not the object) dedups: SSR no-ops via the composable's client guard, client
// hydration fires once, navigating to another event refires, and same-id
// enrichment refreshes do not refire.
const { trackViewContent } = useMetaPixel();
watch(
  () => event.value?.id,
  (id) => {
    if (id) {
      trackViewContent({
        eventId: id,
        valueCents: event.value?.pricing.purchase.total ?? null,
        currency: event.value?.currency ?? 'USD',
        eventID: crypto.randomUUID(),
      });
    }
  },
  { immediate: true },
);

const {
  viewerRole,
  commitment,
  purchase,
  registration,
  claimedAttendance,
  isEnriching,
  enrich,
} = useEventEnrichment({
  eventId,
  asyncData: data,
  refreshPublicEvent: refresh,
});

const showClaimedAttendee = computed(
  () =>
    registration.value?.is_claimed_attendee === true &&
    claimedAttendance.value !== null,
);

const cta = computed(() =>
  event.value && !showClaimedAttendee.value
    ? ctaFor(event.value, viewerRole.value)
    : null,
);
const showAttendeeStatus = computed(
  () =>
    !showClaimedAttendee.value &&
    viewerRole.value === 'attendee' &&
    purchase.value != null,
);
const showBackerStatus = computed(
  () =>
    !showClaimedAttendee.value &&
    viewerRole.value === 'backer' &&
    commitment.value != null,
);

async function refreshEvent() {
  await refresh();
  await enrich();
}

const {
  showCommitDialog,
  showWithdrawDialog,
  showUpdatePaymentDialog,
  isCommitting,
  isWithdrawing,
  isUpdatingPayment,
  paymentMethods: commitPaymentMethods,
  isLoadingPaymentMethods: isLoadingCommitPaymentMethods,
  handleCardAdded: handleCommitCardAdded,
  snackbar,
  snackbarText,
  snackbarColor,
  openCommitDialog,
  handleCommitSubmit,
  openWithdrawDialog,
  handleWithdrawConfirm,
  openUpdatePaymentDialog,
  handleUpdatePaymentSubmit,
} = useCommitmentActions({
  eventId,
  event,
  commitment,
  enrich,
  refreshPublicEvent: refresh,
});

// Auto-resume the commit dialog after an inline sign-in round-trip (?commit=1).
useCommitResume({ event, viewerRole, isEnriching, openCommitDialog });

// --- Interest ("Interested") ---
const authStore = useAuthStore();
const myInterests = useMyInterests();

// The native interest control: a live toggle for role `user`, a sign-in prompt
// for anon visitors, hidden for organizer/backer/attendee and terminal events.
const interestDescriptor = computed(() =>
  event.value
    ? nativeInterestControlFor(
        event.value,
        viewerRole.value,
        authStore.isAuthenticated,
      )
    : null,
);

// The interest action composable mutates these optimistically, so they must be
// writable refs — not computeds over the async `event`. They seed from the
// event and re-sync on every enrichment (which trues up authoritative values
// after a successful toggle via `refresh: enrich`).
const myRung = ref<InterestRung | null>(event.value?.my_rung ?? null);
const interestedCount = ref(event.value?.interested_count ?? 0);
const goingCount = ref(event.value?.going_count ?? 0);

watch(
  () =>
    [
      event.value?.my_rung,
      event.value?.interested_count,
      event.value?.going_count,
    ] as const,
  ([rung, interested, going]) => {
    myRung.value = rung ?? null;
    interestedCount.value = interested ?? 0;
    goingCount.value = going ?? 0;
  },
);

const {
  isPending: isInterestPending,
  snackbar: interestSnackbar,
  snackbarText: interestSnackbarText,
  snackbarColor: interestSnackbarColor,
  setInterest,
} = useInterestActions({
  source: 'native',
  eventId,
  myRung,
  interestedCount,
  goingCount,
  engagementMeta: () => ({ title: event.value?.title }),
  // Re-fetch authoritative counts/my_rung after a successful toggle.
  refresh: enrich,
});

// Replay an anon interest click after an inline sign-in round-trip
// (?interest=1). Native's marker only ever maps to 'interested', and this only
// fires once enrichment settles AND the now-authenticated viewer has a live
// toggle (setInterest no-ops if they are already 'interested').
useInterestResume({
  ready: computed(
    () => !isEnriching.value && interestDescriptor.value?.action === 'toggle',
  ),
  resume: (rung) => setInterest(rung),
});

// Warm the shared interest store for cross-surface consistency (authed-only,
// client-only — inert otherwise).
onMounted(() => {
  myInterests.ensureUpcoming();
});

const {
  showPurchaseDialog,
  isPurchasing,
  paymentMethods: purchasePaymentMethods,
  isLoadingPaymentMethods: isLoadingPurchasePaymentMethods,
  handleCardAdded: handlePurchaseCardAdded,
  snackbar: purchaseSnackbar,
  snackbarText: purchaseSnackbarText,
  snackbarColor: purchaseSnackbarColor,
  openPurchaseDialog,
  handlePurchaseSubmit,
} = usePurchaseActions({
  eventId,
  purchase,
  enrich,
  refreshPublicEvent: refresh,
});

const {
  showTypeSwitchDialog,
  isSwitchingType,
  snackbar: typeSwitchSnackbar,
  snackbarText: typeSwitchSnackbarText,
  snackbarColor: typeSwitchSnackbarColor,
  openTypeSwitchDialog,
  handleTypeSwitchSubmit,
} = useTypeSwitchActions({
  eventId,
  commitment,
  enrich,
  refreshPublicEvent: refresh,
});

const {
  attendees: partyAttendees,
  isLoadingAttendees,
  isInviting,
  isRescinding,
  inviteError,
  snackbar: inviteSnackbar,
  snackbarText: inviteSnackbarText,
  snackbarColor: inviteSnackbarColor,
  fetchAttendees,
  sendInvite,
  rescindInvite,
  clearError: clearInviteError,
} = useInviteActions({
  commitment,
  purchase,
});

// Fetch the party roster whenever the user's commitment or purchase changes
watch(
  [commitment, purchase],
  ([newCommitment, newPurchase]) => {
    if (newCommitment || newPurchase) {
      fetchAttendees();
    } else {
      partyAttendees.value = [];
    }
  },
  { immediate: true },
);

// Update party size dialog
const showUpdateCommitmentDialog = ref(false);
function openUpdateCommitmentDialog() {
  showUpdateCommitmentDialog.value = true;
}
async function handleCommitmentUpdated() {
  await Promise.all([enrich(), refresh(), fetchAttendees()]);
}

const canInviteAttendees = computed(() => {
  if (!event.value) return false;
  // Cannot invite after ticket sales are closed
  return !isTicketSalesClosed(event.value);
});

const pageTitle = computed(() =>
  event.value ? `${event.value.title} | Arvee Events` : 'Event | Arvee Events',
);

const pageDescription = computed(() =>
  event.value ? buildPageDescription(event.value) : '',
);

const placeholder = useEventPlaceholderImage();

const dateRange = computed(() => {
  if (!event.value) return '';
  return formatDateRange(
    event.value.start_datetime ?? '',
    event.value.end_datetime ?? '',
    event.value.timezone ?? '',
  );
});

const attendeePreviewList = ref<EventAttendeeResponse[]>([]);
const attendeePreviewTotal = ref(0);
const attendeePreviewSeatTotal = ref(0);

const { getAttendees } = useAttendeesApi();

watch(viewerRole, async (role) => {
  if (canViewAttendeeList(role)) {
    try {
      const result = await getAttendees(eventId.value, { per_page: 5 });
      attendeePreviewList.value = result.data;
      attendeePreviewTotal.value = result.meta.total;
      attendeePreviewSeatTotal.value =
        result.meta.total_attendees ?? result.meta.total;
    } catch {
      // Silently fail — attendee preview is non-critical
    }
  }
});

const canonicalUrl = computed(() => `${requestOrigin}/events/${eventId.value}`);

useHead(
  () => ({
    title: pageTitle.value,
    meta: [
      { name: 'description', content: pageDescription.value },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: pageDescription.value },
      {
        property: 'og:image',
        content: event.value
          ? `${requestOrigin}/api/og/events/${eventId.value}`
          : '',
      },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl.value },
    ],
    link: [{ rel: 'canonical', href: canonicalUrl.value }],
    script: event.value
      ? [
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Event',
              name: event.value.title,
              description: stripMarkdownToPlain(event.value.description ?? ''),
              startDate: event.value.start_datetime,
              endDate: event.value.end_datetime,
              image: event.value.cover_image?.hero_url,
              eventStatus: 'https://schema.org/EventScheduled',
              eventAttendanceMode:
                'https://schema.org/OfflineEventAttendanceMode',
              location: {
                '@type': 'Place',
                name:
                  event.value.location_name ??
                  event.value.location_address ??
                  '',
                address:
                  event.value.location_address ??
                  event.value.location_name ??
                  '',
                ...(event.value.obfuscated_location_lat != null &&
                event.value.obfuscated_location_lng != null
                  ? {
                      geo: {
                        '@type': 'GeoCoordinates',
                        latitude: event.value.obfuscated_location_lat,
                        longitude: event.value.obfuscated_location_lng,
                      },
                    }
                  : {}),
              },
              ...(event.value.organizer
                ? {
                    organizer: {
                      '@type': 'Person',
                      name: event.value.organizer.name,
                    },
                  }
                : {}),
            }),
          },
        ]
      : [],
  }),
  { tagPriority: 10 },
);
</script>

<template>
  <div>
    <!-- Loading State -->
    <template
      v-if="
        !event && (status === 'pending' || status === 'idle' || isEnriching)
      "
    >
      <v-skeleton-loader type="image" class="mb-6" />
      <v-row>
        <v-col cols="12" md="8">
          <v-skeleton-loader type="heading" class="mb-4" />
          <v-skeleton-loader type="paragraph" class="mb-4" />
          <v-skeleton-loader type="paragraph" />
        </v-col>
        <v-col cols="12" md="4">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </template>

    <!-- Content (data wins over status) -->
    <template v-else-if="event">
      <v-btn
        variant="text"
        prepend-icon="mdi-arrow-left"
        :to="viewerRole === 'organizer' ? '/dashboard' : '/browse'"
        class="mb-2"
      >
        {{ viewerRole === 'organizer' ? 'Back to Dashboard' : 'Browse Events' }}
      </v-btn>

      <!-- Cover Image -->
      <v-img
        :src="event.cover_image?.hero_url ?? placeholder"
        :alt="`Cover image for ${event.title}`"
        :aspect-ratio="16 / 9"
        cover
        rounded="lg"
        class="bg-surface-variant mb-6 mx-auto"
        style="max-width: 800px"
      />

      <v-row>
        <!-- Main Content -->
        <v-col cols="12" md="8">
          <div class="d-flex align-center ga-3 mb-4">
            <h1 class="text-h4">{{ event.title }}</h1>
            <EventsEventStatusBadge :status="event.user_facing_status" />
          </div>

          <p class="text-subtitle-1 text-medium-emphasis mb-2">
            {{ dateRange }} &middot;
            {{ event.location_name ?? event.location_address ?? '' }}
          </p>
          <p class="text-body-2 text-medium-emphasis mb-6">
            Organized by {{ event.organizer?.name ?? 'Event Organizer' }}
          </p>

          <EventsEventDetailsSection :event="event" />
          <EventsEventExpensesSection
            v-if="event.expenses?.length"
            :expenses="event.expenses"
          />
          <EventsEventRulesSection :rules="event.rules ?? null" />
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="4">
          <v-progress-linear
            v-if="isEnriching"
            indeterminate
            height="2"
            color="primary"
            class="mb-1"
          />
          <EventsEventPricingCard
            v-if="event.pricing"
            :event="event"
            class="mb-4"
          />
          <!-- Role-dependent actions: wait for enrichment to avoid flashing wrong state -->
          <template v-if="!isEnriching">
            <!-- Social proof: shown to every viewer, reflects optimistic toggles -->
            <EventsSocialProofCounts
              :interested-count="interestedCount"
              :going-count="goingCount"
              class="mb-4"
            />
            <ClientOnly v-if="viewerRole === 'organizer'">
              <EventsOrganizerActionsPanel
                :event="event"
                :viewer-role="viewerRole"
                :refresh-event="refreshEvent"
              />
            </ClientOnly>
            <EventsAttendeeStatusCard
              v-else-if="showAttendeeStatus"
              :purchase="purchase!"
              :event="event"
              @purchase-additional="openPurchaseDialog"
            />
            <EventsBackerStatusCard
              v-else-if="showBackerStatus"
              :commitment="commitment!"
              :event="event"
              @update-payment-method="openUpdatePaymentDialog"
              @withdraw="openWithdrawDialog"
              @switch-type="openTypeSwitchDialog"
              @update-party-size="openUpdateCommitmentDialog"
              @purchase-additional="openPurchaseDialog"
            />
            <EventsClaimedAttendeeView
              v-else-if="showClaimedAttendee && claimedAttendance"
              :event="event"
              :attendance="claimedAttendance"
            />
            <EventsGuestAuthPanel
              v-else-if="cta && cta.action === 'login'"
              :event-id="event.id"
            />
            <EventsEventCtaModule
              v-else-if="cta"
              :cta="cta"
              :event-id="event.id"
              @commit="openCommitDialog"
              @purchase="openPurchaseDialog"
              @withdraw="openWithdrawDialog"
            />

            <!-- Party roster: shown to backers/attendees who have a registration -->
            <EventsPartyRosterCard
              v-if="
                (showBackerStatus || showAttendeeStatus) &&
                partyAttendees.length > 0
              "
              :attendees="partyAttendees"
              :is-loading="isLoadingAttendees"
              :is-inviting="isInviting"
              :is-rescinding="isRescinding"
              :invite-error="inviteError"
              :can-invite="canInviteAttendees"
              class="mt-4"
              @invite-submit="sendInvite"
              @rescind="rescindInvite"
              @clear-error="clearInviteError"
            />

            <!-- Interest toggle: only visible for role user/visitor and never
                 alongside the backer/attendee status cards. -->
            <EventsEventInterestControl
              v-if="interestDescriptor?.visible"
              :descriptor="interestDescriptor"
              :event-id="eventId"
              :pending="isInterestPending"
              class="mt-4"
              @select="setInterest"
            />
          </template>
          <ClientOnly>
            <EventsEventAttendeeModule
              v-if="attendeePreviewTotal > 0"
              :attendees="attendeePreviewList"
              :total-count="attendeePreviewTotal"
              :total-attendees="attendeePreviewSeatTotal"
              :event-id="eventId"
            />
          </ClientOnly>
        </v-col>
      </v-row>

      <!-- Commitment & Purchase Dialogs -->
      <ClientOnly>
        <EventsCommitmentDialog
          v-model="showCommitDialog"
          :event="event"
          :is-submitting="isCommitting"
          :payment-methods="commitPaymentMethods"
          :is-loading-methods="isLoadingCommitPaymentMethods"
          @submit="handleCommitSubmit"
          @card-added="handleCommitCardAdded"
        />
        <EventsPurchaseDialog
          v-model="showPurchaseDialog"
          :event="event"
          :is-submitting="isPurchasing"
          :payment-methods="purchasePaymentMethods"
          :is-loading-methods="isLoadingPurchasePaymentMethods"
          :existing-type-id="
            commitment?.registration_type_id ?? purchase?.registration_type_id
          "
          @submit="handlePurchaseSubmit"
          @card-added="handlePurchaseCardAdded"
        />
        <EventsUpdatePaymentMethodDialog
          v-model="showUpdatePaymentDialog"
          :is-submitting="isUpdatingPayment"
          :payment-methods="commitPaymentMethods"
          :is-loading-methods="isLoadingCommitPaymentMethods"
          @submit="handleUpdatePaymentSubmit"
          @card-added="handleCommitCardAdded"
        />
      </ClientOnly>
      <EventsWithdrawConfirmDialog
        v-model="showWithdrawDialog"
        :is-loading="isWithdrawing"
        @confirm="handleWithdrawConfirm"
      />
      <ClientOnly>
        <EventsTypeSwitchDialog
          v-if="event && commitment"
          v-model="showTypeSwitchDialog"
          :event="event"
          :commitment="commitment"
          :is-submitting="isSwitchingType"
          @submit="handleTypeSwitchSubmit"
        />
        <EventsUpdateCommitmentDialog
          v-if="event && commitment"
          v-model="showUpdateCommitmentDialog"
          :event="event"
          :commitment="commitment"
          :attendees="partyAttendees"
          @updated="handleCommitmentUpdated"
        />
      </ClientOnly>

      <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
        {{ snackbarText }}
      </v-snackbar>
      <v-snackbar
        v-model="purchaseSnackbar"
        :color="purchaseSnackbarColor"
        :timeout="4000"
      >
        {{ purchaseSnackbarText }}
      </v-snackbar>
      <v-snackbar
        v-model="typeSwitchSnackbar"
        :color="typeSwitchSnackbarColor"
        :timeout="4000"
      >
        {{ typeSwitchSnackbarText }}
      </v-snackbar>
      <v-snackbar
        v-model="inviteSnackbar"
        :color="inviteSnackbarColor"
        :timeout="4000"
      >
        {{ inviteSnackbarText }}
      </v-snackbar>
      <v-snackbar
        v-model="interestSnackbar"
        :color="interestSnackbarColor"
        :timeout="4000"
      >
        {{ interestSnackbarText }}
      </v-snackbar>
    </template>

    <!-- Error State (only when no data AND not enriching) -->
    <v-alert
      v-else-if="status === 'error'"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      <div class="d-flex align-center justify-space-between">
        <span>Failed to load event. Please try again.</span>
        <v-btn variant="text" color="error" @click="refresh()">Retry</v-btn>
      </div>
    </v-alert>
  </div>
</template>
